import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFilter } from '../FilterContext/FilterContext';
import { updateShipment, exportDataToExcel } from '../../services/api';
import './genericTable.css';
import Loader from '../Loader/Loader';
import FilterForm from '../FilterForm/FilterForm';
import PreFacComponent from '../PreFacComponent/PreFacComponent';
import InvoiceCommands from '../InvoiceCommands/InvoiceCommands';
import CarrierBillingComponent from '../CarrierBillingComponent/CarrierBillingComponent';
import EnCoursCommands from '../EnCoursCommands/EnCoursCommands';
import MessageBox from '../MessageBox/MessageBox';
import ClientInvoicesComponent from '../ClientInvoicesComponent/ClientInvoicesComponent';
import IconButton from '../IconButton/IconButton';
import CachedIcon from '@mui/icons-material/Cached';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { useRouteFilters } from '../../hooks/useRouteFilters';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { ROUTE_REQUIRED_FILTERS, CONTEXT_KEYS } from '../../config/routeFilters';

const GenericTable = ({
  data_type,
  tableTitle,
  data_identifier,
  columns,
  fetchFunction,
  url,
  headerClass,
}) => {
  const navigate = useNavigate();

  // Contexte des filtres et sorting
  const { setFilters } = useFilter();

  const [data, setData] = useState([]);
  const [lastImportedPage, setLastImportedPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState(1);
  const [numberOnPage, setNumberOnPage] = useState(0);
  const [pageInfo, setPageInfo] = useState({
    total_objects: 0,
    total_pages: 0,
    nextPageExists: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { isReady: filtersReady } = useRouteFilters(searchParams, setSearchParams);
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = searchParams.get('page_size') || 200;
  const sort = searchParams.get('sort') || '';
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [showInvoiceCommands, setShowInvoiceCommands] = useState(false);
  const [invoiceClicked, setInvoiceClicked] = useState(null);
  const [showEnCoursCommands, setShowEnCoursCommands] = useState(false);
  const [enCoursCommandsIndex, setEnCoursCommandsIndex] = useState('');
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxConfig, setMessageBoxConfig] = useState({
    message: '',
    type: 'ok',
    onConfirm: null,
    onCancel: () => setShowMessageBox(false),
    onClose: () => setShowMessageBox(false),
    loading_button: false,
    input: false,
  });

  // États pour la sélection de cellules (somme type Excel)
  const [selectedCells, setSelectedCells] = useState([]);
  const [selectionSum, setSelectionSum] = useState(null);
  const [isCtrlHeld, setIsCtrlHeld] = useState(false);
  const selectionStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragCellsRef = useRef([]);
  const blockNavigationRef = useRef(false);
  const tableContentRef = useRef(null);

  const { handleResizeStart, resetWidths, getColumnStyle } = useColumnWidths(
    url,
    searchParams,
    columns
  );

  // Détecter si Ctrl est enfoncé + Echap pour effacer la sélection
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlHeld(true);
      }
      if (e.key === 'Escape') {
        setSelectedCells([]);
        setSelectionSum(null);
        isDraggingRef.current = false;
      }
    };
    const handleKeyUp = e => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlHeld(false);
      }
    };
    const handleBlur = () => setIsCtrlHeld(false);

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Désélectionner si clic en dehors du tableau
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        selectionSum !== null &&
        tableContentRef.current &&
        !tableContentRef.current.contains(e.target)
      ) {
        setSelectedCells([]);
        setSelectionSum(null);
        isDraggingRef.current = false;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectionSum]);

  // Permet d'utiliser await(secondes)
  const delay = ms => new Promise(res => setTimeout(res, ms));

  const calculateTotals = () => {
    const totals = {};

    columns.forEach(col => {
      if (col.type === 'currency' || col.type === 'number') {
        totals[col.dataField] = data.reduce((acc, item) => {
          const value = parseFloat(item[col.dataField]) || 0;
          return acc + value;
        }, 0);
      }
    });

    return totals;
  };

  const totals = calculateTotals();

  const getInvoiceClass = entry => {
    let invoice_class = '';
    if (data_type === 'invoice') {
      const today = new Date();
      if (
        entry['document_type'] === 'invoice' &&
        entry['due_date'] &&
        new Date(entry['due_date']) <= today &&
        entry['status'] === 'NON RÉGLÉE'
      ) {
        invoice_class = 'overdue-invoice-marker';
      } else if (entry['document_type'] === 'credit' && entry['status'] === 'NON UTILISÉ') {
        invoice_class = 'available-credit-marker';
      } else if (
        entry['document_type'] === 'invoice' &&
        entry['status'] === 'NON RÉGLÉE / RECOUVREMENT'
      ) {
        invoice_class = 'collection-marker';
      }
    }
    return invoice_class;
  };

  const fetchData = async filters => {
    setIsLoading(true);
    let elements_count = 0;

    try {
      const fetchedData = await fetchFunction(currentPage, filters, sort, pageSize);

      if (fetchedData && Array.isArray(fetchedData.results)) {
        setData(fetchedData.results);
        setNumberOnPage(fetchedData.results.length);
        elements_count = fetchedData.results.length;
        setPageInfo({
          total_objects: fetchedData.total_objects,
          total_pages: fetchedData.total_pages,
          nextPageExists: fetchedData.next === null,
        });
        setLastImportedPage(currentPage);
      } else {
        setData([]);
        console.error('La réponse ne contient pas de tableau "results"');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      setData([]);
    }
    if (elements_count <= 100) {
      await delay(100);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!filtersReady) {
      return;
    }

    const newFilterInputs = {};

    searchParams.forEach((value, key) => {
      if (value !== '') {
        newFilterInputs[key] = value;
      }
    });

    fetchData(newFilterInputs);

    setFilters(searchParams.toString());

    setRefresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, refresh, filtersReady]);

  useEffect(() => {
    if (invoiceClicked !== null) {
      setShowInvoiceCommands(true);
    }
  }, [invoiceClicked]);

  const handlePageInputChange = e => {
    if (e.target.value) {
      setSearchParams({ ...searchParams, page: e.target.value });
    } else {
      e.preventDefault();
    }
  };

  const handlePageInputBlur = e => {
    if (e.target.value <= pageInfo.total_pages && e.target.value > 0) {
      if (e.target.value !== lastImportedPage) {
        setSearchParams({ ...searchParams, page: e.target.value }); // Mise à jour de page dans les paramètres de recherche
      }
    } else {
      setPageInputValue(lastImportedPage);
    }
  };

  const handlePrevPage = e => {
    const newPageNumber = Math.max(1, currentPage - 1);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPageNumber);
    setSearchParams(newSearchParams);
    setPageInputValue(newPageNumber);
  };

  const handleNextPage = e => {
    const newPageNumber = currentPage + 1;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPageNumber);
    setSearchParams(newSearchParams);
    setPageInputValue(newPageNumber);
  };

  const handlePageSizeSelector = e => {
    if (e.target.value) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page_size', e.target.value);
      setSearchParams(newSearchParams);
    } else {
      e.preventDefault();
    }
  };

  const handleFilterClick = (event, column) => {
    event.stopPropagation();
    setActiveFilterColumn(activeFilterColumn === column ? null : column);
  };

  const applyFilters = (...filters) => {
    const newSearchParams = new URLSearchParams(searchParams);
    filters.forEach(([field, value]) => {
      value !== '' ? newSearchParams.set(field, value) : newSearchParams.delete(field);
    });
    setSearchParams(newSearchParams);
  };

  const closeForm = () => {
    setActiveFilterColumn(null);
  };

  const removeFilters = (...fields) => {
    const newSearchParams = new URLSearchParams(searchParams);
    fields.forEach(([field]) => {
      newSearchParams.delete(field);
    });
    setSearchParams(newSearchParams);
  };

  const handleRemoveDateFilters = async field => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(`${field}_gte`);
    newSearchParams.delete(`${field}_lte`);
    setSearchParams(newSearchParams);
  };

  // const handleFilterInputChange = (field, value) => {
  //     // setFilterInputs(prev => ({
  //     //     ...prev,
  //     //     [field]: value
  //     // }));
  //     const newSearchParams = new URLSearchParams(searchParams);
  //     value ? newSearchParams.set(field, value) : newSearchParams.delete(field);
  //     setSearchParams(newSearchParams);
  // };

  const handleSortChange = (field, order) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('sort', order === 'asc' ? field : `-${field}`);
    setSearchParams(newSearchParams);
  };

  const handleRefresh = () => {
    setRefresh(true);
  };

  const handleReinit = () => {
    const newSearchParams = new URLSearchParams();
    const basePath = url.replace(/\/$/, '') || '/';
    const requiredFilters = ROUTE_REQUIRED_FILTERS[basePath] || {};

    for (const contextKey of CONTEXT_KEYS) {
      const value = searchParams.get(contextKey);
      if (value) {
        newSearchParams.set(contextKey, value);
      }
    }

    for (const [key, value] of Object.entries(requiredFilters)) {
      newSearchParams.set(key, value);
    }

    newSearchParams.set('page', '1');
    if (pageSize === -1) {
      newSearchParams.set('page_size', 200);
    }

    setSearchParams(newSearchParams);
  };

  const handleDataClick = data_identifier => {
    if (data_type === 'invoice') {
      setInvoiceClicked(data_identifier);
    } else {
      navigate(`${url}${data_identifier}`);
    }
  };

  const handleCloseInvoiceCommands = () => {
    setInvoiceClicked(null);
    setShowInvoiceCommands(false);
    setRefresh(true);
  };

  const handleShowEnCoursCommands = (e, index) => {
    if (data[index].shipment_stage === 'EN COURS') {
      e.preventDefault();
      setEnCoursCommandsIndex(index);
      setShowEnCoursCommands(true);
    }
  };

  const handleMarkAsPickedUp = async (index, picked_up) => {
    const updatedFormData = {
      shipment_number: data[index].shipment_number,
      picked_up_marker: picked_up,
    };
    try {
      const response = await updateShipment(updatedFormData);
      if (response.status === 200) {
        setRefresh(true);
      }
      setShowEnCoursCommands(false);
    } catch (error) {
      setMessageBoxConfig({
        message: `Erreur lors de la mise à jour des données picked_up_marker ${picked_up}: ${error}.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleMarkAsDelivered = async index => {
    const updatedFormData = {
      shipment_number: data[index].shipment_number,
      shipment_stage: 'LIVRÉ',
    };
    try {
      const response = await updateShipment(updatedFormData);
      if (response.status === 200) {
        setRefresh(true);
      }
      setShowEnCoursCommands(false);
    } catch (error) {
      setMessageBoxConfig({
        message: `Erreur lors de la mise à jour des données shipment_stage LIVRÉ: ${error}.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleDownloadData = async () => {
    const exportData = async filters => {
      try {
        await exportDataToExcel(data_type, filters, sort);
      } catch (error) {
        setMessageBoxConfig({
          message: `Une erreur est survenue lors de l'export de l'excel: ${error}`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    };

    const newFilterInputs = {};
    searchParams.forEach((value, key) => {
      if (value !== '') {
        newFilterInputs[key] = value;
      }
    });

    setMessageBoxConfig({
      message:
        'Votre téléchargement est en cours de traitement et va commencer dans quelques secondes.',
      type: 'ok',
      loading_button: false,
      input: false,
      onConfirm: () => setShowMessageBox(false),
      onClose: () => setShowMessageBox(false),
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);

    await exportData(newFilterInputs);
  };

  const handleResetWidths = () => {
    setMessageBoxConfig({
      message: 'Voulez-vous réinitialiser les largeurs de colonnes ?',
      type: 'yesno',
      loading_button: false,
      input: false,
      onConfirm: () => {
        resetWidths();
        setShowMessageBox(false);
      },
      onCancel: () => setShowMessageBox(false),
      onClose: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const formatValue = (value, type) => {
    const isoDateTimeRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
    const isIsoDateTime = str => isoDateTimeRegex.test(str);
    const formatDateOnly = isoDateString => {
      const date = new Date(isoDateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    };

    const formatCurrency = value => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    if (value && type) {
      if (type === 'percentage') {
        return `${(value * 100).toFixed(2)}%`;
      } else if (isIsoDateTime(value) || type === 'date') {
        return formatDateOnly(value);
      } else if (type === 'currency') {
        return formatCurrency(value);
      }
    }
    return value;
  };

  const isNumericColumn = dataField => {
    const column = columns.find(col => col.dataField === dataField);
    return column && ['currency', 'number', 'percentage'].includes(column.type);
  };

  const calculateDragCells = (startRow, startCol, endRow, endCol) => {
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);

    const cells = [];
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const col = columns[c];
        if (col && isNumericColumn(col.dataField)) {
          const cellValue = parseFloat(data[r]?.[col.dataField]) || 0;
          cells.push({ rowIndex: r, colDataField: col.dataField, value: cellValue });
        }
      }
    }
    return cells;
  };

  const updateDragVisual = cells => {
    // Retirer l'ancienne sélection visuelle
    tableContentRef.current?.querySelectorAll('.cell-dragging').forEach(el => {
      el.classList.remove('cell-dragging');
    });
    // Ajouter la nouvelle
    cells.forEach(cell => {
      const cellEl = tableContentRef.current?.querySelector(
        `[data-cell-key="${cell.rowIndex}-${cell.colDataField}"]`
      );
      cellEl?.classList.add('cell-dragging');
    });
  };

  const handleCellMouseDown = (e, rowIndex, colDataField, value) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (!isNumericColumn(colDataField)) return;

    const cellValue = parseFloat(value) || 0;
    const colIndex = columns.findIndex(col => col.dataField === colDataField);
    selectionStartRef.current = { rowIndex, colDataField, colIndex, value: cellValue };
    isDraggingRef.current = false;
    dragCellsRef.current = [{ rowIndex, colDataField, value: cellValue }];
    blockNavigationRef.current = true;

    updateDragVisual(dragCellsRef.current);
  };

  const handleCellMouseEnter = (e, rowIndex, colDataField, value) => {
    if (!selectionStartRef.current) return;
    if (!isNumericColumn(colDataField)) return;

    const start = selectionStartRef.current;
    const endColIndex = columns.findIndex(col => col.dataField === colDataField);

    if (start.rowIndex !== rowIndex || start.colIndex !== endColIndex) {
      isDraggingRef.current = true;
    }

    dragCellsRef.current = calculateDragCells(
      start.rowIndex,
      start.colIndex,
      rowIndex,
      endColIndex
    );
    updateDragVisual(dragCellsRef.current);
  };

  const handleCellMouseUp = () => {
    const start = selectionStartRef.current;
    if (!start) return;

    // Nettoyer le visuel de drag
    tableContentRef.current?.querySelectorAll('.cell-dragging').forEach(el => {
      el.classList.remove('cell-dragging');
    });

    if (isDraggingRef.current && dragCellsRef.current.length > 0) {
      // Drag : ajouter les cellules du rectangle à la sélection
      const newCells = dragCellsRef.current.filter(
        newCell =>
          !selectedCells.some(
            cell => cell.rowIndex === newCell.rowIndex && cell.colDataField === newCell.colDataField
          )
      );
      const mergedCells = [...selectedCells, ...newCells];
      setSelectedCells(mergedCells);
      const sum = mergedCells.reduce((acc, cell) => acc + cell.value, 0);
      setSelectionSum(sum);
    } else {
      // Clic simple : ajouter/retirer la cellule
      const { rowIndex, colDataField, value } = start;
      const existingIndex = selectedCells.findIndex(
        cell => cell.rowIndex === rowIndex && cell.colDataField === colDataField
      );

      let newSelectedCells;
      if (existingIndex >= 0) {
        newSelectedCells = selectedCells.filter((_, i) => i !== existingIndex);
      } else {
        newSelectedCells = [...selectedCells, { rowIndex, colDataField, value }];
      }

      setSelectedCells(newSelectedCells);
      if (newSelectedCells.length > 0) {
        const sum = newSelectedCells.reduce((acc, cell) => acc + cell.value, 0);
        setSelectionSum(sum);
      } else {
        setSelectionSum(null);
      }
    }

    selectionStartRef.current = null;
    isDraggingRef.current = false;
    dragCellsRef.current = [];
  };

  const handleTableMouseLeave = () => {
    if (selectionStartRef.current && isDraggingRef.current && dragCellsRef.current.length > 0) {
      tableContentRef.current?.querySelectorAll('.cell-dragging').forEach(el => {
        el.classList.remove('cell-dragging');
      });
      // Ajouter les cellules du rectangle à la sélection
      const newCells = dragCellsRef.current.filter(
        newCell =>
          !selectedCells.some(
            cell => cell.rowIndex === newCell.rowIndex && cell.colDataField === newCell.colDataField
          )
      );
      const mergedCells = [...selectedCells, ...newCells];
      setSelectedCells(mergedCells);
      const sum = mergedCells.reduce((acc, cell) => acc + cell.value, 0);
      setSelectionSum(sum);
    }
    selectionStartRef.current = null;
    isDraggingRef.current = false;
    dragCellsRef.current = [];
  };

  const clearSelection = () => {
    setSelectedCells([]);
    setSelectionSum(null);
    isDraggingRef.current = false;
  };

  const handleHeaderClick = (e, column) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (!isNumericColumn(column.dataField)) return;

    e.stopPropagation();

    const columnCells = data.map((entry, rowIndex) => ({
      rowIndex,
      colDataField: column.dataField,
      value: parseFloat(entry[column.dataField]) || 0,
    }));

    const newCells = columnCells.filter(
      newCell =>
        !selectedCells.some(
          cell => cell.rowIndex === newCell.rowIndex && cell.colDataField === newCell.colDataField
        )
    );

    const mergedCells = [...selectedCells, ...newCells];
    setSelectedCells(mergedCells);
    const sum = mergedCells.reduce((acc, cell) => acc + cell.value, 0);
    setSelectionSum(sum);
  };

  const selectedCellsSet = useMemo(() => {
    if (selectionSum === null) return new Set();
    return new Set(selectedCells.map(cell => `${cell.rowIndex}-${cell.colDataField}`));
  }, [selectedCells, selectionSum]);

  const topRightCellKey = useMemo(() => {
    if (selectedCells.length === 0 || selectionSum === null) return null;

    // Priorité : colonne la plus à droite, puis ligne la plus haute
    const maxColIndex = Math.max(
      ...selectedCells.map(c => columns.findIndex(col => col.dataField === c.colDataField))
    );
    const rightColCells = selectedCells.filter(
      c => columns.findIndex(col => col.dataField === c.colDataField) === maxColIndex
    );
    const minRow = Math.min(...rightColCells.map(c => c.rowIndex));
    const topRightCell = rightColCells.find(c => c.rowIndex === minRow);

    return topRightCell ? `${topRightCell.rowIndex}-${topRightCell.colDataField}` : null;
  }, [selectedCells, selectionSum, columns]);

  return (
    <div className="table-container">
      <div className={`table-header ${headerClass}`}>
        <IconButton
          icon={CachedIcon}
          text=""
          onClick={handleRefresh}
          variant="contained"
          styleClass={'round-white'}
          title="Rafraîchir"
        />
        <span className="table-header separator"></span>
        <IconButton
          icon={ClearIcon}
          text=""
          onClick={handleReinit}
          variant="contained"
          styleClass={'round-white'}
          title="Réinitialiser les filtres"
        />
        <span className="table-header separator"></span>
        <IconButton
          icon={SettingsBackupRestoreIcon}
          text=""
          onClick={handleResetWidths}
          variant="contained"
          styleClass={'round-white'}
          title="Réinitialiser les largeurs de colonnes"
        />
        <h2>{tableTitle}</h2>
        <IconButton
          icon={DownloadIcon}
          text=""
          onClick={handleDownloadData}
          variant="contained"
          styleClass={'round-white table-header-dowbload-button'}
          title="Télécharger les données filtrées"
        />
      </div>
      {url === '/pre-fac/' && (
        <div className="table-sub-heading-component-container">
          <PreFacComponent
            searchParams={searchParams}
            applyFilters={applyFilters}
            removeFilters={removeFilters}
            onRefresh={() => setRefresh(true)}
          />
        </div>
      )}
      {url === '/factures/' && (
        <div className="table-sub-heading-component-container">
          <ClientInvoicesComponent />
        </div>
      )}
      {url === '/facturation-tps/' && (
        <div className="table-sub-heading-component-container">
          <CarrierBillingComponent
            searchParams={searchParams}
            applyFilters={applyFilters}
            removeFilters={removeFilters}
          />
        </div>
      )}
      <div
        ref={tableContentRef}
        className={`table-content ${
          url === '/pre-fac/' || url === '/facturation-tps/' || url === '/factures/'
            ? 'table-sub-heading-component'
            : ''
        } ${data_type !== 'shipment' ? 'wide-cells' : ''}`}
      >
        <table>
          {isLoading ? (
            <thead></thead>
          ) : (
            <thead>
              <tr>
                {columns.map(col => (
                  <th
                    key={col.dataField}
                    data-column={col.dataField}
                    style={getColumnStyle(col.dataField)}
                    onClick={e => handleHeaderClick(e, col)}
                    className={
                      isNumericColumn(col.dataField) && isCtrlHeld ? 'header-selectable' : ''
                    }
                  >
                    <div className="column-header">{col.name}</div>
                    <div className="filter-buttons-container">
                      <button
                        className="filter-button"
                        onClick={event => handleFilterClick(event, col.dataField)}
                      >
                        Filtrer
                      </button>
                      {searchParams.get(col.dataField) &&
                        !(url === '/pre-fac/' && col.dataField === 'invoicing_status') &&
                        !(url === '/supprimes/' && col.dataField === 'shipment_is_active') && (
                          <button
                            className="filter-remove"
                            onClick={() => removeFilters([col.dataField])}
                          ></button>
                        )}
                      {(searchParams.get(`${col.dataField}_gte`) ||
                        searchParams.get(`${col.dataField}_lte`)) && (
                        <button
                          className="filter-remove"
                          onClick={() => handleRemoveDateFilters(col.dataField)}
                        ></button>
                      )}
                    </div>
                    {activeFilterColumn === col.dataField && (
                      <FilterForm
                        data_type={data_type}
                        field={col.dataField}
                        translated_field={col.name}
                        applyFilters={applyFilters}
                        fieldName={col.dataField}
                        fieldType={col.type}
                        searchParams={searchParams}
                        sort={sort}
                        handleSortChange={handleSortChange}
                        closeForm={closeForm}
                      />
                    )}
                    <div
                      className="resize-handle"
                      onMouseDown={e => handleResizeStart(e, col.dataField)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          {isLoading ? (
            <tbody></tbody>
          ) : (
            <tbody onMouseLeave={handleTableMouseLeave} className={isCtrlHeld ? 'ctrl-mode' : ''}>
              {data.map((entry, index) => (
                <tr
                  onClick={() => {
                    if (blockNavigationRef.current) {
                      blockNavigationRef.current = false;
                      return;
                    }
                    if (!isDraggingRef.current) {
                      handleDataClick(entry[data_identifier]);
                    }
                  }}
                  onMouseUp={handleCellMouseUp}
                  key={entry[data_identifier]}
                  className={`${
                    entry['shipment_stage'] === 'EN COURS' && entry['picked_up_marker']
                      ? 'picked-up-marker'
                      : ''
                  } ${data_type === 'invoice' ? getInvoiceClass(entry) : ''} ${
                    data_type === 'carrier' && entry['is_favorite'] ? 'favorite-carrier' : ''
                  }`}
                >
                  {columns.map(column => {
                    const isLegacyInvoice =
                      column.dataField === 'invoice_number' && entry.legacy_invoice_number;
                    const displayValue = isLegacyInvoice
                      ? entry.legacy_invoice_number.substring(0, 7) + '...'
                      : column.type === 'boolean'
                        ? entry[column.dataField]
                          ? 'Oui'
                          : 'Non'
                        : formatValue(entry[column.dataField] || '', column.type);
                    const tooltipValue = isLegacyInvoice
                      ? entry.legacy_invoice_number
                      : entry[column.dataField] !== undefined && entry[column.dataField] !== null
                        ? entry[column.dataField].toString()
                        : '';
                    const cellValue = entry[column.dataField];

                    const cellKey = `${index}-${column.dataField}`;

                    const isNumeric = isNumericColumn(column.dataField);

                    return (
                      <td
                        key={column.dataField}
                        data-column={column.dataField}
                        style={getColumnStyle(column.dataField)}
                        data-cell-key={cellKey}
                        className={`${column.type === 'text' && 'text-cell'} ${
                          (['boolean', 'number', 'date'].includes(column.type) ||
                            ['rate', 'phone', 'postcode', 'shipment_number', 'invoice_number'].some(
                              str => column.dataField.includes(str)
                            )) &&
                          'text-centered'
                        } ${column.bold ? 'bold-cell' : ''} ${
                          selectedCellsSet.has(cellKey) ? 'cell-selected' : ''
                        } ${isNumeric ? 'numeric-cell' : ''}`}
                        title={tooltipValue}
                        onContextMenu={e => handleShowEnCoursCommands(e, index)}
                        onMouseDown={e =>
                          handleCellMouseDown(e, index, column.dataField, cellValue)
                        }
                        onMouseEnter={e =>
                          handleCellMouseEnter(e, index, column.dataField, cellValue)
                        }
                      >
                        <div className="overflow-cell">{displayValue}</div>
                        {topRightCellKey === cellKey && (
                          <span
                            className="cell-selection-close"
                            onMouseDown={e => {
                              e.stopPropagation();
                              clearSelection();
                            }}
                          >
                            ✕
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {(data_type === 'shipment' || data_type === 'invoice') && (
                <tr className="totals-row non-clickable-row" onClick={() => console.log(totals)}>
                  {columns.map(column => (
                    <td
                      key={column.dataField}
                      data-column={column.dataField}
                      style={getColumnStyle(column.dataField)}
                      className="text-centered"
                    >
                      <div className="overflow-cell">
                        {(column.type === 'number' &&
                          column.dataField !== 'pallet_height' &&
                          column.dataField !== 'footage') ||
                        column.type === 'currency'
                          ? formatValue(totals[column.dataField], column.type)
                          : column.dataField === data_identifier
                            ? 'TOTAUX'
                            : ''}
                      </div>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          )}
        </table>
        {isLoading && (
          <div>
            <Loader />
          </div>
        )}
        {selectionSum !== null && (
          <div className="selection-sum-tooltip" onClick={clearSelection}>
            <span className="selection-sum-label">Somme ({selectedCells.length} cellules) :</span>
            <span className="selection-sum-value">{formatValue(selectionSum, 'currency')}</span>
            <span className="selection-sum-close">✕</span>
          </div>
        )}
      </div>
      <div className="table-footer">
        <div className="page-selectors">
          <label>Nombre d'éléments par page :</label>
          <select onChange={handlePageSizeSelector} value={pageSize}>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
            {/* { pageInfo.total_objects <= 4000 &&
                            <option value={-1}>Tous</option>
                        } */}
          </select>
          <p>{`${(currentPage - 1) * pageSize} - ${
            (currentPage - 1) * pageSize + numberOnPage
          } sur ${pageInfo.total_objects} (${pageInfo.total_pages} ${
            pageInfo.total_pages !== 1 ? 'pages' : 'page'
          })`}</p>
          <button
            className="arrow-button prev"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          ></button>
          <input
            className="input-page"
            type="number"
            value={pageInputValue}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
          ></input>
          <button
            className="arrow-button next"
            onClick={handleNextPage}
            disabled={pageInfo.nextPageExists}
          ></button>
        </div>
      </div>

      {showMessageBox && (
        <MessageBox
          message={messageBoxConfig.message}
          onClose={() => setShowMessageBox(false)}
          onConfirm={messageBoxConfig.onConfirm}
          onCancel={messageBoxConfig.onCancel}
          type={messageBoxConfig.type}
          loading_button={messageBoxConfig.loading_button}
          input={messageBoxConfig.input}
          technicalDetails={messageBoxConfig.technicalDetails}
        />
      )}
      {showInvoiceCommands && (
        <InvoiceCommands invoice_number={invoiceClicked} onClose={handleCloseInvoiceCommands} />
      )}
      {showEnCoursCommands && (
        <EnCoursCommands
          index={enCoursCommandsIndex}
          shipment_number={data[enCoursCommandsIndex].shipment_number}
          picked_up={data[enCoursCommandsIndex].picked_up_marker}
          onMarkAsPickedUp={handleMarkAsPickedUp}
          onMarkAsDelivered={handleMarkAsDelivered}
          onClose={() => setShowEnCoursCommands(false)}
        />
      )}
    </div>
  );
};

export default GenericTable;
